import moment from 'moment';

// ASP.NET serialises `DateTime` values as "2026-08-28T03:01:07" — no
// trailing "Z". `moment.utc(...)` parses strings without an explicit
// offset as UTC by default, which is exactly the intent for
// server-stored instants. We keep using moment.utc here so this
// entity stays portable to any caller that needs either UTC or
// local-time formatting via `.format(...)` / `.local().format(...)`.

// Lightweight SignalFile entity wrapper with PascalCase fields only
export default class SignalFile {
  constructor(data) {
    // Expect data to have PascalCase fields (Id, ModelId, ModelFileName, ModelName, CreateTime, Signals)
    this.Id = data.Id;
    this.ModelId = data.ModelId;
    this.ModelFileName = data.ModelFileName;
    this.ModelName = data.ModelName;
    this.Signals = data.Signals || [];
    this.Tag = data.Tag || null;
    this.Active = data.Active || false;
    this.CalculationId = data.CalculationId || null;
    this.RealtimeCandlesUsed = data.RealtimeCandlesUsed || false;

    // Convert CreateTime to a moment instance (UTC) once, here, so the
    // entity is fully ready to format right after construction.
    // Invalid / missing values collapse to null.
    if (data.CreateTime == null) {
      this.CreateTime = null;
    } else {
      const parsed = moment.utc(data.CreateTime);
      this.CreateTime = parsed.isValid() ? parsed : null;
    }
  }

  // Helper method to format the CreateTime as a local-time string.
  // This is the single source of truth for rendering SignalFile
  // creation timestamps — used by both /signal-files (the main list
  // "Created" column and the View modal's "Created" line) and
  // /model-execution. Render in local time so the admin sees the
  // wall-clock moment the file was produced in their own zone.
  getFormattedCreateTime() {
    return this.CreateTime ? this.CreateTime.local().format('L LTS') : '';
  }

  // Get signals count
  getSignalsCount() {
    return this.Signals ? this.Signals.length : 0;
  }

  // Get signal action counts
  getSignalActionCounts() {
    if (!this.Signals || this.Signals.length === 0) {
      return { Buy: 0, Sell: 0, Money: 0 };
    }

    const counts = { Buy: 0, Sell: 0, Money: 0 };
    this.Signals.forEach(signal => {
      if (counts.hasOwnProperty(signal.Action)) {
        counts[signal.Action]++;
      }
    });

    return counts;
  }

  // Format signals for display. Signal `Time` values are trading-day
  // dates stored by the server as UTC (e.g. "2025-04-10T00:00:00"),
  // and the /signal-files page renders them in UTC to stay aligned
  // with the candle chart and the API response. We therefore format
  // them with the UTC zone here (not `.local()`) so every caller —
  // /model-execution's "Show Signals" expansion included — shows the
  // same wall-clock date the server stored.
  //
  // Returned shape is the original signal plus a `FormattedTime`
  // string. The date-only variant is exposed separately so callers
  // that want a compact "M/D/YYYY" rendering (the /signal-files
  // per-signal table) do not have to reimplement the formatter.
  getFormattedSignals() {
    if (!this.Signals || this.Signals.length === 0) return [];

    return this.Signals.map(signal => {
      const m = moment.utc(signal.Time);
      return {
        ...signal,
        FormattedTime: m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss [UTC]') : '',
        FormattedDate: m.isValid() ? m.format('M/D/YYYY') : '',
      };
    });
  }

  // Builds a UTC `YYYY-MM-DD` grouping key for a signal's `Time`.
  // Used by the /signal-files side-by-side table to bucket signals
  // per trading day; routing through moment.utc keeps the key in
  // sync with `FormattedDate` so a row in the table and the same
  // date's signals always agree.
  static getSignalDateKey(timeStr) {
    if (!timeStr) return '';
    const m = moment.utc(timeStr);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }

  // Get unique signal actions
  getUniqueActions() {
    if (!this.Signals || this.Signals.length === 0) return [];

    const actions = new Set(this.Signals.map(signal => signal.Action));
    return Array.from(actions);
  }
}
